import { EnhancedArtifact } from '../../types/artifacts';
import { ChatThread } from '../../contexts/ChatContext';
import { ModelType } from '../../types/models';

export interface PinnedArtifact {
  id: string;
  artifactId: string;
  artifact: EnhancedArtifact;
  pinnedAt: string;
  pinnedBy: string;
  notes?: string;
}

export interface FocusTask {
  id: string;
  title: string;
  description: string;
  type: 'artifact' | 'process' | 'validation' | 'improvement' | 'urgent';
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  dueDate?: string;
  assigneeId: string;
  assigneeName: string;
  artifactId?: string;
  artifactName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SampleSidebarService {
  getPinnedArtifacts: (artifacts: EnhancedArtifact[]) => Promise<PinnedArtifact[]>;
  getFocusTasks: () => Promise<FocusTask[]>;
  getSampleThreads: () => Promise<ChatThread[]>;
}

class SampleSidebarServiceImpl implements SampleSidebarService {
  async getPinnedArtifacts(artifacts: EnhancedArtifact[]): Promise<PinnedArtifact[]> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 100));

    // Select 3 interesting artifacts to pin (prioritize different types)
    const businessEntities = artifacts.filter(a => a.type === 'business_entity');
    const processes = artifacts.filter(a => a.type === 'process');
    const solutions = artifacts.filter(a => a.type === 'solution');
    const improvements = artifacts.filter(a => a.type === 'improvement');

    const pinnedArtifacts: PinnedArtifact[] = [];

    // Pin one of each type, prioritizing the most interesting ones
    if (businessEntities.length > 0) {
      const artifact = businessEntities[0]; // "Operating Checking"
      pinnedArtifacts.push({
        id: `pinned-${artifact.id}`,
        artifactId: artifact.id,
        artifact,
        pinnedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
        pinnedBy: 'admin',
        notes: 'High priority financial account - needs regular monitoring'
      });
    }

    if (processes.length > 0) {
      const artifact = processes[0]; // "Treasury: Reconcile Accounts"
      pinnedArtifacts.push({
        id: `pinned-${artifact.id}`,
        artifactId: artifact.id,
        artifact,
        pinnedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
        pinnedBy: 'admin',
        notes: 'Critical monthly process - due next week'
      });
    }

    if (improvements.length > 0) {
      const artifact = improvements[0]; // "Improvement Initiative"
      pinnedArtifacts.push({
        id: `pinned-${artifact.id}`,
        artifactId: artifact.id,
        artifact,
        pinnedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
        pinnedBy: 'admin',
        notes: 'Active improvement project - high impact potential'
      });
    }

    // If we don't have enough variety, fill with other types
    if (pinnedArtifacts.length < 3 && solutions.length > 0) {
      const artifact = solutions[0]; // "Customer Support"
      pinnedArtifacts.push({
        id: `pinned-${artifact.id}`,
        artifactId: artifact.id,
        artifact,
        pinnedAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(), // 6 hours ago
        pinnedBy: 'admin',
        notes: 'Customer satisfaction tracking - monitor closely'
      });
    }

    return pinnedArtifacts;
  }

  async getFocusTasks(): Promise<FocusTask[]> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 100));

    const now = new Date();
    const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    const focusTasks: FocusTask[] = [
      {
        id: 'task-1',
        title: 'Review Financial Reconciliation Process',
        description: 'Monthly reconciliation process needs review and optimization. Current process takes too long.',
        type: 'process',
        priority: 'high',
        status: 'in_progress',
        dueDate: nextWeek.toISOString(),
        assigneeId: 'u1',
        assigneeName: 'Finance Manager',
        artifactId: 'a4',
        artifactName: 'Treasury: Reconcile Accounts',
        createdAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(now.getTime() - 1 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'task-2',
        title: 'Validate Business Entity Relationships',
        description: 'Ensure all business entities have proper relationships and dependencies defined.',
        type: 'validation',
        priority: 'medium',
        status: 'pending',
        dueDate: tomorrow.toISOString(),
        assigneeId: 'u2',
        assigneeName: 'Business Analyst',
        artifactId: 'a1',
        artifactName: 'Operating Checking',
        createdAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'task-3',
        title: 'Improve Customer Support Metrics',
        description: 'Current resolution rate is 85%. Target is 90%. Need to identify bottlenecks.',
        type: 'improvement',
        priority: 'high',
        status: 'in_progress',
        assigneeId: 'u3',
        assigneeName: 'Support Lead',
        artifactId: 'a6',
        artifactName: 'Customer Support',
        createdAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(now.getTime() - 4 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'task-4',
        title: 'Update Employee Management Process',
        description: 'New HR policies require updates to employee management workflows.',
        type: 'artifact',
        priority: 'medium',
        status: 'pending',
        dueDate: nextWeek.toISOString(),
        assigneeId: 'u4',
        assigneeName: 'HR Manager',
        artifactId: 'a5',
        artifactName: 'Employee Management',
        createdAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'task-5',
        title: 'Critical: Lead Generation Process Failure',
        description: 'Lead generation process has failed 3 times this week. Immediate attention required.',
        type: 'urgent',
        priority: 'critical',
        status: 'pending',
        assigneeId: 'u5',
        assigneeName: 'Marketing Manager',
        artifactId: 'a7',
        artifactName: 'Lead Generation Process',
        createdAt: new Date(now.getTime() - 2 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(now.getTime() - 30 * 60 * 1000).toISOString(),
      }
    ];

    return focusTasks;
  }

  async getSampleThreads(): Promise<ChatThread[]> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 100));

    const now = new Date();
    
    const sampleThreads: ChatThread[] = [
      {
        id: 'thread-financial-analysis',
        name: 'Financial Analysis',
        description: 'Discussion about financial processes and reconciliation',
        createdAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(now.getTime() - 3 * 60 * 60 * 1000).toISOString(),
        messageCount: 12,
        isPinned: true,
        tags: ['finance', 'process', 'analysis'],
        participants: ['u1', 'u2'],
        model: { type: ModelType.COMPANY },
        uiRole: 'operator',
        lastMessage: {
          id: 'msg-financial-last',
          threadId: 'thread-financial-analysis',
          who: 'assistant',
          text: 'The reconciliation process analysis is complete. Found 3 optimization opportunities.',
          timestamp: new Date(now.getTime() - 3 * 60 * 60 * 1000).toISOString(),
          type: 'text',
        }
      },
      {
        id: 'thread-customer-improvement',
        name: 'Customer Experience Improvements',
        description: 'Exploring ways to enhance customer support and satisfaction',
        createdAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(now.getTime() - 1 * 60 * 60 * 1000).toISOString(),
        messageCount: 8,
        isPinned: false,
        tags: ['customer', 'improvement', 'support'],
        participants: ['u3', 'u4'],
        model: { type: ModelType.COMPANY },
        uiRole: 'coach',
        lastMessage: {
          id: 'msg-customer-last',
          threadId: 'thread-customer-improvement',
          who: 'user',
          text: 'What are the current customer satisfaction metrics?',
          timestamp: new Date(now.getTime() - 1 * 60 * 60 * 1000).toISOString(),
          type: 'text',
        }
      },
      {
        id: 'thread-process-optimization',
        name: 'Process Optimization',
        description: 'Identifying and improving inefficient business processes',
        createdAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(now.getTime() - 30 * 60 * 1000).toISOString(),
        messageCount: 15,
        isPinned: true,
        tags: ['process', 'optimization', 'efficiency'],
        participants: ['u1', 'u3', 'u5'],
        model: { type: ModelType.COMPANY },
        uiRole: 'architect',
        lastMessage: {
          id: 'msg-process-last',
          threadId: 'thread-process-optimization',
          who: 'assistant',
          text: 'Process mapping complete. Ready to discuss optimization strategies.',
          timestamp: new Date(now.getTime() - 30 * 60 * 1000).toISOString(),
          type: 'text',
        }
      },
      {
        id: 'thread-market-analysis',
        name: 'Market Analysis & Growth',
        description: 'Analyzing market trends and growth opportunities',
        createdAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(now.getTime() - 12 * 60 * 60 * 1000).toISOString(),
        messageCount: 6,
        isPinned: false,
        tags: ['market', 'growth', 'analysis'],
        participants: ['u5', 'u6'],
        model: { type: ModelType.COMPANY },
        uiRole: 'operator',
        lastMessage: {
          id: 'msg-market-last',
          threadId: 'thread-market-analysis',
          who: 'assistant',
          text: 'Market size analysis shows 12% growth potential in target segment.',
          timestamp: new Date(now.getTime() - 12 * 60 * 60 * 1000).toISOString(),
          type: 'text',
        }
      }
    ];

    return sampleThreads;
  }
}

// Export singleton instance
export const sampleSidebarService = new SampleSidebarServiceImpl();
